<template>
  <div class="row">
    <div class="col-12">
      <advanced-filter
        ref="advancedFilter"
        key="invoice-filter"
        :refs="$refs"
        :select-filters="computedSelectFilters"
        :date-range="true"
        :after-change="afterFilterChange"
        @change="filterChanged"
      />

      <v-server-table
        v-if="initialised"
        ref="table"
        :columns="columns"
        :options="options"
        :url="url"
      >
        <template #download="{ row }">
          <a
            href=""
            target="_blank"
            class="btn btn-sm btn-primary"
            @click.prevent="downloadPdf(row)"
          >
            <span v-if="generatingPdf.includes(row.id)">
              <font-awesome-icon spin :icon="['fal', 'spinner']" />
            </span>
            <span v-else>
              <font-awesome-icon :icon="['fal', 'cloud-download']" />
            </span>
          </a>
        </template>
      </v-server-table>
    </div>
  </div>
</template>

<script type="text/babel">
/* eslint-disable vue/no-deprecated-destroyed-lifecycle */
import entity from "../../mixins/entity";
import format from "../../mixins/format";
import AdvancedFilter from "../../components/common/AdvancedFilter.vue";

const columns = [
  "invoice_number",
  "job_contact",
  "billing_contact",
  "status",
  "payment_method",
  "voucher_code",
  "total",
  "created_at",
  "delivery_status",
  "download",
];

export default {
  components: {
    AdvancedFilter,
  },
  mixins: [entity, format],

  data() {
    return {
      initialised: false,
      generatingPdf: [],

      showingQuote: "0",
      storedBrands: {
        options: [],
        value: "_all",
        allConfig: {
          label: "Show All Brands",
          value: "_all",
        },
      },

      columns: [],
      options: {
        advancedFilter: true,
        orderBy: { ascending: false },
        sortable: [
          "invoice_number",
          "status",
          "voucher_code",
          "total",
          "created_at",
          "delivery_status",
        ],
        columnsClasses: {
          download: "text-center",
          actions: "text-center",
        },
        templates: {
          invoice_number: (h, o) =>
            h("invoice-object", {
              props: {
                invoice: o,
                entityId: o.id,
              },
            }),
          total: (h, o) => this.formatCurrency(o.total),
          job_contact: (h, o) => this.$lodash.get(o, "job.job_contacts[0].name").trim(),
          billing_contact: (h, o) => {
            if (this.$lodash.has(o, "job.billing_contacts[0].id")) {
              return this.$lodash.get(o, "job.billing_contacts[0].name").trim();
            }

            return this.$lodash.get(o, "job.job_contacts[0].name").trim();
          },
          created_at: (h, o) => this.formatLocalDate(o.created_at),
          actions: (h, o) => this.entityDelete(h, o, "invoices", this.$refs),
        },
      },
    };
  },

  computed: {
    url() {
      return "/invoices?with=job.jobContacts;job.billingContacts&orderBy=id&sortedBy=desc";
    },

    computedSelectFilters() {
      const invoiceFilters = {
        status: {
          options: [
            {
              label: "Pending",
              value: "Pending",
            },
            {
              label: "Sent",
              value: "Sent",
            },
            {
              label: "Paid",
              value: "Paid",
            },
            {
              label: "Refunded",
              value: "Refunded",
            },
          ],
        },
      };

      const quoteFilters = {
        accepted: {
          options: [
            {
              label: "Yes",
              value: "1",
            },
            {
              label: "No",
              value: "0",
            },
          ],
        },
        processed: {
          options: [
            {
              label: "Yes",
              value: "1",
            },
            {
              label: "No",
              value: "0",
            },
          ],
        },
      };

      let filters = {
        brand: this.storedBrands,
        is_quote: {
          value: "0",
          options: [
            {
              label: "Quotes",
              value: "1",
            },
            {
              label: "Invoices",
              value: "0",
            },
          ],
        },
      };

      switch (this.showingQuote) {
        case "0":
          filters = { ...filters, ...invoiceFilters };
          break;

        case "1":
          filters = { ...filters, ...quoteFilters };
          break;

        default:
          filters = { ...filters, ...invoiceFilters, ...quoteFilters };
      }

      if (!this.$can("read", "invoices", "filter-by-brand")) {
        delete filters.brand;
      }

      return filters;
    },
  },

  beforeCreate() {
    this.$store.commit("advanced-filter/resetSelectFilters");
  },

  created() {
    this.init();
  },

  methods: {
    async init() {
      this.columns = columns;

      const brands = await this.$store.dispatch("brands/getData");

      brands.forEach((brand) => {
        this.storedBrands.options = this.storedBrands.options.concat({
          label: brand.name,
          value: brand.id,
        });
      });

      if (this.$can("delete", "invoices") && !this.columns.includes("actions")) {
        this.columns.push("actions");
      }

      this.initialised = true;
    },

    downloadPdf(invoice) {
      if (this.generatingPdf.includes(invoice.id)) {
        return;
      }

      this.generatingPdf.push(invoice.id);

      this.$http
        .get(`/invoices/${invoice.id}/pdf`, { responseType: "arraybuffer" })
        .then((response) => {
          // noinspection JSUnresolvedFunction
          const blob = new Blob([response.data], { type: response.headers.get("content-type") });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = `${invoice.is_quote ? "Quote" : "Invoice"} ${invoice.invoice_number}.pdf`;
          link.click();

          this.generatingPdf = this.generatingPdf.filter((value) => value !== invoice.id);
        });
    },

    filterChanged(event) {
      if (event && event.name && event.name === "is_quote") {
        this.showingQuote = event.value;
        this.$forceUpdate(); // Need this line otherwise table flickers to empty, not sure why
      }
    },

    afterFilterChange(name, value, selectFilterValues) {
      if (name === "is_quote") {
        switch (value) {
          case "0":
            delete selectFilterValues.accepted;
            delete selectFilterValues.processed;
            selectFilterValues["status"] = "";
            break;

          case "1":
            delete selectFilterValues.status;
            selectFilterValues["accepted"] = "";
            selectFilterValues["processed"] = "";
            break;

          default:
            selectFilterValues["status"] = "";
            selectFilterValues["accepted"] = "";
            selectFilterValues["processed"] = "";
        }
      }
    },
  },
};
</script>
